import React, { useState } from "react";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import type { Product } from "@bmi/elasticsearch-types";
import { constructQueryForProductReferences } from "../helpers/esQueries";
import { queryElasticSearch } from "../../../utils/elasticSearch";
import {
  getVergeOption,
  transformProductReferences
} from "../helpers/products";
import { devLog } from "../../../utils/devLog";
import { useSiteContext } from "../../Site";
import { CalculatorSteps, mainTileReferencesMapper } from "../types";
import { useConfig } from "../../../contexts/ConfigProvider";
import { useAnalyticsContext } from "../helpers/analytics";
import type {
  Data,
  ReferencedTileProducts,
  Tile,
  TileOptionSelections
} from "../types";
import type { ESResponse } from "../../../types/elasticsearch";

const useVariantSelection = ({
  mainTileCode,
  tiles,
  setSelectedStep,
  setLoading,
  setTileOptions
}: {
  mainTileCode?: string;
  tiles: Data["tiles"];
  setSelectedStep: (step: CalculatorSteps) => void;
  setLoading: (isLoading: boolean) => void;
  setTileOptions: (tileOptions?: TileOptionSelections) => void;
}) => {
  const [variant, setVariant] = useState<Tile | undefined>(undefined);
  const { getMicroCopy } = useSiteContext();
  const { esIndexNameProduct } = useConfig();
  const pushEvent = useAnalyticsContext();

  const selectVariant = async (
    _: React.FormEvent,
    { variant: newVariantCode }: { variant: string }
  ) => {
    if (!esIndexNameProduct || !mainTileCode) {
      return;
    }

    pushEvent({
      event: "dxb.button_click",
      id: "rc-select-tile-colour",
      label: getMicroCopy(microCopy.VARIANT_SELECTION_NEXT_LABEL),
      action: "selected"
    });

    if (newVariantCode === variant?.externalProductCode) {
      setSelectedStep(CalculatorSteps.TileOptions);
      return;
    }

    // eslint-disable-next-line security/detect-object-injection
    const newVariant = tiles[mainTileCode].find(
      (variant) => variant.externalProductCode === newVariantCode
    )!;

    setVariant(newVariant);
    setTileOptions(undefined);

    if (!newVariant.productReferences?.length) {
      setVariant(newVariant);
      setSelectedStep(CalculatorSteps.TileOptions);
      return;
    }

    try {
      setLoading(true);
      const query = constructQueryForProductReferences(
        newVariant.productReferences
      );
      const res: ESResponse<Product> = await queryElasticSearch(
        query,
        esIndexNameProduct
      );
      const hits = res.hits.hits.map((hit) => hit._source);
      const productReferences =
        transformProductReferences<ReferencedTileProducts>(
          newVariant.productReferences,
          hits,
          mainTileReferencesMapper
        );

      const {
        left,
        right,
        halfLeft,
        halfRight,
        leftVergeTileType7,
        rightVergeTileType7,
        leftVergeTileType9,
        rightVergeTileType9,
        ...rest
      } = productReferences;

      setVariant({
        ...newVariant,
        ...rest,
        vergeOption: getVergeOption({
          left,
          right,
          halfLeft,
          halfRight,
          leftVergeTileType7,
          rightVergeTileType7,
          leftVergeTileType9,
          rightVergeTileType9
        })
      });
      setSelectedStep(CalculatorSteps.TileOptions);
    } catch (err) {
      devLog("Failed to fetch supplementary products", err);
    }
    setLoading(false);
  };

  return { selectVariant, variant, setVariant };
};

export default useVariantSelection;
